"use client";

import { useState, useCallback, useEffect } from "react";
import type { BaseEntity } from "../../UniversalCRMTable/types";

// ============================================================================
// FIELD EDITING HOOK
// ============================================================================

export interface UseFieldEditingReturn<T extends BaseEntity> {
  editingField: string | null;
  editedItem: T;
  isSaving: boolean;
  handleFieldEdit: (fieldId: string) => void;
  handleSaveField: (fieldId: string, newValue?: unknown) => Promise<void>;
  handleCancelEdit: () => void;
  handleFieldValueChange: (fieldId: string, newValue: unknown) => void;
}

export function useFieldEditing<T extends BaseEntity>(
  item: T,
  onInlineEdit?: (item: T) => Promise<void>
): UseFieldEditingReturn<T> {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editedItem, setEditedItem] = useState<T>(item);
  const [isSaving, setIsSaving] = useState(false);

  // Sync editedItem with item when item changes (but preserve local edits)
  useEffect(() => {
    // Only update if we're not currently editing
    if (editingField === null) {
      setEditedItem(item);
    }
  }, [item, editingField]);

  const handleFieldEdit = useCallback((fieldId: string) => {
    setEditingField(fieldId);
  }, []);

  const handleSaveField = useCallback(
    async (fieldId: string, newValue?: unknown) => {
      try {
        setIsSaving(true);

        if (onInlineEdit) {
          // Use a state setter to get the current editedItem value
          // This avoids including editedItem in the dependency array
          // which would cause the callback to recreate on every keystroke
          setEditedItem((currentEditedItem) => {
            const itemToSave =
              newValue !== undefined
                ? { ...item, ...currentEditedItem, [fieldId]: newValue }
                : { ...item, ...currentEditedItem };

            // Call the mutation with the correct item
            onInlineEdit(itemToSave).catch((error) => {
              console.error("Failed to save field:", error);
              // On error, revert to the original item
              setEditedItem(item);
              // Keep the field in editing mode so user can retry
              setEditingField(fieldId);
            });

            // Return the updated item for state
            return itemToSave;
          });

          // Optimistic update: immediately clear editing state
          setEditingField(null);
        } else {
          // If no onInlineEdit callback, just clear the editing state
          setEditingField(null);
        }
      } catch (error) {
        console.error("Failed to save field:", error);
        // On error, revert to the original item
        setEditedItem(item);
        // Keep the field in editing mode so user can retry
        setEditingField(fieldId);
      } finally {
        setIsSaving(false);
      }
    },
    [item, onInlineEdit]
  );

  const handleCancelEdit = useCallback(() => {
    setEditedItem(item);
    setEditingField(null);
  }, [item]);

  const handleFieldValueChange = useCallback((fieldId: string, newValue: unknown) => {
    setEditedItem((prev) => ({ ...prev, [fieldId]: newValue }));
  }, []);

  return {
    editingField,
    editedItem,
    isSaving,
    handleFieldEdit,
    handleSaveField,
    handleCancelEdit,
    handleFieldValueChange,
  };
}
